import React, { useState, useEffect } from 'react';
import './ActionEditor.css';

const ActionEditor = ({ action = {}, devices = [], scenes = [], onSave, onCancel }) => {
  const [type, setType] = useState(action.type || 'device');
  
  // Device action fields
  const [device, setDevice] = useState(action.device || '');
  const [devicePayload, setDevicePayload] = useState(action.payload || {});
  const [payloadKey, setPayloadKey] = useState('');
  const [payloadValue, setPayloadValue] = useState('');
  const [parentId, setParentId] = useState(action.parentId || '');
  
  // Scene action fields
  const [sceneId, setSceneId] = useState(action.sceneId || '');
  const [silent, setSilent] = useState(action.silent || false);
  
  // Notification action fields
  const [message, setMessage] = useState(action.message || '');
  const [level, setLevel] = useState(action.level || 'info');

  // Get device attributes when device changes
  const [deviceAttributes, setDeviceAttributes] = useState([]);
  
  useEffect(() => {
    if (device) {
      // Find the selected device
      const selectedDevice = devices.find(d => d.id === device);
      
      if (selectedDevice) {
        // Extract available attributes from device
        const attributes = [];
        
        // Add all properties from controls
        if (selectedDevice.controls) {
          selectedDevice.controls.forEach(control => {
            if (control.property && !attributes.includes(control.property)) {
              attributes.push(control.property);
            }
          });
        }
        
        setDeviceAttributes(attributes);
        
        // Check if this is a child device
        if (selectedDevice.parentId && selectedDevice.parentId !== device) {
          setParentId(selectedDevice.parentId);
        } else {
          setParentId('');
        }
      }
    }
  }, [device, devices]);

  const handleAddPayloadItem = () => {
    if (payloadKey && payloadValue) {
      setDevicePayload(prev => ({
        ...prev,
        [payloadKey]: payloadValue
      }));
      setPayloadKey('');
      setPayloadValue('');
    }
  };

  const handleRemovePayloadItem = (key) => {
    const newPayload = { ...devicePayload };
    delete newPayload[key];
    setDevicePayload(newPayload);
  };

  const handleSave = () => {
    let newAction = { type };
    
    if (type === 'device') {
      newAction = {
        ...newAction,
        device,
        payload: devicePayload
      };
      
      // Only include parentId if it exists
      if (parentId) {
        newAction.parentId = parentId;
      }
    } else if (type === 'scene') {
      newAction = {
        ...newAction,
        sceneId,
        silent
      };
    } else if (type === 'notification') {
      newAction = {
        ...newAction,
        message,
        level
      };
    }
    
    onSave(newAction);
  };

  return (
    <div className="action-editor">
      <div className="form-group">
        <label htmlFor="action-type">Action Type</label>
        <select
          id="action-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="device">Control Device</option>
          <option value="scene">Activate Scene</option>
          <option value="notification">Show Notification</option>
        </select>
      </div>
      
      {type === 'device' && (
        <>
          <div className="form-group">
            <label htmlFor="device">Device</label>
            <select
              id="device"
              value={device}
              onChange={(e) => {
                const deviceId = e.target.value;
                setDevice(deviceId);
                if (deviceId) {
                  const selectedDevice = devices.find(d => d.id === deviceId);
                  if (selectedDevice && selectedDevice.parentId && selectedDevice.parentId !== deviceId) {
                    setParentId(selectedDevice.parentId);
                  } else {
                    setParentId('');
                  }
                }
              }}
              required
            >
              <option value="">Select a device</option>
              {devices.map(d => (
                <option key={d.id} value={d.id}>
                   {`${d.name} (${d.location || 'N/A'}, Floor ${d.floor || 'N/A'})`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="device-payload">
            <h4>Device Payload</h4>
            
            <div className="form-row">
              <div className="form-group flex-grow">
                <label htmlFor="payload-key">Property</label>
                <select
                  id="payload-key"
                  value={payloadKey}
                  onChange={(e) => setPayloadKey(e.target.value)}
                  disabled={!device}
                >
                  <option value="">Select a property</option>
                  {deviceAttributes.map(attr => (
                    <option key={attr} value={attr}>
                      {attr}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group flex-grow">
                <label htmlFor="payload-value">Value</label>
                <input
                  id="payload-value"
                  type="text"
                  value={payloadValue}
                  onChange={(e) => setPayloadValue(e.target.value)}
                  disabled={!payloadKey}
                />
              </div>
              
              <button 
                type="button" 
                className="add-payload-btn"
                onClick={handleAddPayloadItem}
                disabled={!payloadKey || !payloadValue}
              >
                Add
              </button>
            </div>
            
            <div className="payload-items">
              {Object.keys(devicePayload).length === 0 ? (
                <p className="no-payload">No properties defined</p>
              ) : (
                Object.entries(devicePayload).map(([key, value]) => (
                  <div key={key} className="payload-item">
                    <span className="payload-key">{key}:</span>
                    <span className="payload-value">{value}</span>
                    <button 
                      type="button"
                      onClick={() => handleRemovePayloadItem(key)}
                      className="remove-payload-btn"
                    >
                      &times;
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
      
      {type === 'scene' && (
        <>
          <div className="form-group">
            <label htmlFor="scene-id">Scene</label>
            <select
              id="scene-id"
              value={sceneId}
              onChange={(e) => setSceneId(e.target.value)}
              required
            >
              <option value="">Select a scene</option>
              {scenes.map(scene => (
                <option key={scene.id} value={scene.id}>
                  {scene.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={silent}
                onChange={(e) => setSilent(e.target.checked)}
              />
              Silent (do not show notifications)
            </label>
          </div>
        </>
      )}
      
      {type === 'notification' && (
        <>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <input
              id="message"
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="level">Level</label>
            <select
              id="level"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </select>
          </div>
        </>
      )}
      
      <div className="action-editor-actions">
        <button 
          type="button" 
          className="cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button 
          type="button" 
          className="save-btn"
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default ActionEditor;